/**
 * Hook for withdrawing SOL from on-chain PDA to wallet
 *
 * Temporary direct blockchain implementation until backend API is ready.
 * Withdraws SOL directly from the user's PDA account to their wallet.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PublicKey, Transaction, TransactionMessage, VersionedTransaction } from '@solana/web3.js';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { getConnection } from '@/lib/solana/anchorClient';
import { buildWithdrawTransaction } from '@/lib/solana/withdrawTransaction';
import { formatLamportsToSol } from '@/utils/solana/lamportsToSol';
import { logger, getGenericErrorMessage } from '@/lib/logger';
import { isMobileWallet } from '@/lib/responsive';
import { captureException, createErrorContext } from '@/lib/sentry';
import { parseSolanaError, isUserActionableError } from '@/utils/solanaErrorParser';

interface WithdrawResult {
  signature: string;
  amount: bigint;
}

async function withdrawSol(
  amountLamports: bigint,
  walletProvider: any,
  walletAddress: string
): Promise<WithdrawResult> {
  const errorContext = createErrorContext('withdraw', walletAddress, {
    amountLamports: amountLamports.toString(),
    amountSol: Number(amountLamports) / 1e9,
    isPhantom: walletProvider?.isPhantom === true,
  });

  if (amountLamports <= BigInt(0)) {
    const err = new Error('Withdraw amount must be greater than 0');
    captureException(err, errorContext);
    throw err;
  }

  const isMobile = isMobileWallet();
  // Use signAndSendTransaction for Phantom wallets (both embedded and extension support it)
  // Embedded wallets ONLY support signAndSendTransaction, so we use it for all Phantom to be safe
  const isPhantomWallet = walletProvider.isPhantom === true;
  const amountSol = Number(amountLamports) / 1e9;
  logger.log(`💸 [Withdraw] Platform: ${isMobile ? 'Mobile' : 'Desktop'}, Wallet: ${isPhantomWallet ? 'Phantom' : 'Other'}, Amount: ${amountSol} SOL`);

  const connection = getConnection();
  const walletPubkey = new PublicKey(walletAddress);

  // Step 1: Build withdrawal transaction instructions
  logger.log('📋 [Withdraw] Building withdrawal transaction...');
  const instructions = await buildWithdrawTransaction(walletPubkey, amountLamports);

  // Step 2: Get blockhash (needed for both paths)
  logger.log('🔗 [Withdraw] Getting fresh blockhash...');
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  // For Phantom wallets, use signAndSendTransaction (works for both embedded and extension)
  // This is required for embedded wallets and also works for extension wallets
  if (isPhantomWallet && walletProvider.signAndSendTransaction) {
    logger.log('✍️ [Withdraw] Using signAndSendTransaction for Phantom wallet...');

    // Create VersionedTransaction for embedded wallet
    const messageV0 = new TransactionMessage({
      payerKey: walletPubkey,
      recentBlockhash: blockhash,
      instructions: instructions,
    }).compileToV0Message();

    const versionedTx = new VersionedTransaction(messageV0);

    // signAndSendTransaction handles both signing and sending atomically
    const result = await walletProvider.signAndSendTransaction(versionedTx);
    const signature = result.signature;

    logger.log('✅ [Withdraw] Transaction signed and sent:', signature);

    // Wait for confirmation with timeout
    // Phantom's signAndSendTransaction uses their RPC, which may differ from ours
    logger.log('⏳ [Withdraw] Waiting for confirmation...');

    const CONFIRMATION_TIMEOUT_MS = 30000; // 30 seconds

    try {
      // Race between confirmation and timeout
      await Promise.race([
        connection.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight
        }, 'confirmed'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('CONFIRMATION_TIMEOUT')), CONFIRMATION_TIMEOUT_MS)
        )
      ]);

      logger.log(`🎉 [Withdraw] Confirmed: ${signature}`);
    } catch (confirmError: any) {
      if (confirmError.message === 'CONFIRMATION_TIMEOUT') {
        // Timeout - check if transaction actually exists
        logger.warn('⚠️ [Withdraw] Confirmation timeout, verifying transaction...');

        const txInfo = await connection.getTransaction(signature, {
          commitment: 'confirmed',
          maxSupportedTransactionVersion: 0
        });

        if (txInfo) {
          // Transaction exists and confirmed
          if (txInfo.meta?.err) {
            const err = new Error(`Transaction failed: ${JSON.stringify(txInfo.meta.err)}`);
            captureException(err, { ...errorContext, stage: 'phantom_tx_failed', signature, txError: txInfo.meta.err });
            throw err;
          }
          logger.log(`🎉 [Withdraw] Verified confirmed: ${signature}`);
        } else {
          // Transaction doesn't exist - Phantom may have failed to submit
          logger.error('❌ [Withdraw] Transaction not found on chain');
          const err = new Error(
            'Transaction was not submitted to the network. ' +
            'This may be due to Phantom wallet configuration. Please try again or use a different wallet.'
          );
          captureException(err, { ...errorContext, stage: 'phantom_tx_not_found', signature });
          throw err;
        }
      } else {
        captureException(confirmError, { ...errorContext, stage: 'phantom_confirm_error', signature });
        throw confirmError;
      }
    }

    return {
      signature,
      amount: amountLamports,
    };
  }

  // For extension wallets, use existing signTransaction + sendRawTransaction flow
  const transaction = new Transaction();
  transaction.feePayer = walletPubkey;
  transaction.recentBlockhash = blockhash;
  transaction.add(...instructions);
  // Gate Wallet (and some injected wallets) require the transaction to have an explicit signer list
  // populated (signatures array contains the fee payer with a null signature) before they enable "Sign".
  transaction.setSigners(walletPubkey);

  // Sign transaction with user's wallet (wallet popup happens here)
  logger.log('✍️ [Withdraw] Requesting wallet signature...');

  // If the provider supports `sendTransaction`, prefer it (WalletConnect/AppKit providers often require this).
  if (!isPhantomWallet && typeof walletProvider?.sendTransaction === 'function') {
    try {
      const messageV0 = new TransactionMessage({
        payerKey: walletPubkey,
        recentBlockhash: blockhash,
        instructions,
      }).compileToV0Message();
      const versionedTx = new VersionedTransaction(messageV0);

      const signature = await walletProvider.sendTransaction(versionedTx as any, connection as any, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
        maxRetries: 3,
      });

      await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight,
      }, 'confirmed');

      return { signature, amount: amountLamports };
    } catch (sendError: any) {
      throw sendError;
    }
  }

  const signedTx = await walletProvider.signTransaction(transaction);
  logger.log('✅ [Withdraw] Transaction signed');

  // Send transaction to blockchain
  logger.log('📡 [Withdraw] Sending transaction to blockchain...');
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });

  // Wait for confirmation
  logger.log('⏳ [Withdraw] Waiting for confirmation...');
  await connection.confirmTransaction({
    signature,
    blockhash,
    lastValidBlockHeight
  }, 'confirmed');

  logger.log(`🎉 [Withdraw] Confirmed: ${signature}`);

  return {
    signature,
    amount: amountLamports,
  };
}

export function useWithdraw() {
  const { address, isConnected, walletProvider } = useWalletAddress();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (amountLamports: bigint) => {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }

      return withdrawSol(amountLamports, walletProvider, address);
    },
    onSuccess: (data) => {
      const withdrawnSol = formatLamportsToSol(data.amount);
      toast.success(`Successfully withdrew ${withdrawnSol} SOL from your Junk Fun account!`);

      // Invalidate queries to refresh user balance
      queryClient.invalidateQueries({ queryKey: ['userProgress', address] });
      // Invalidate SOL history to show new withdrawal transaction
      queryClient.invalidateQueries({ queryKey: ['solHistory', address] });
    },
    onError: (error: Error, amountLamports: bigint) => {
      logger.error('Withdraw error:', error);
      captureException(error, {
        operation: 'withdraw_fallback',
        amountLamports: amountLamports.toString(),
        walletAddress: address,
      });

      const parsedError = parseSolanaError(error);

      if (isUserActionableError(parsedError)) {
        const description = parsedError.suggestedAction
          ? `${parsedError.message}\n\n${parsedError.suggestedAction}`
          : parsedError.message;

        toast.error('Withdrawal Failed', {
          description,
          duration: 8000,
        });
      } else {
        toast.error(getGenericErrorMessage(error));
      }
    },
  });
}
